import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Search,
  Filter,
  Eye,
  Edit,
  Download,
  Plus,
  X,
  AlertCircle,
  Stethoscope,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  DollarSign,
  Activity,
  RefreshCw,
  FileText,
  Users,
  IndianRupee,
} from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";
import {
  getAllCheckUp,
  getCheckUpsByDoctor,
} from "../../../services/checkUpApi";
import PrescriptionDownload from "./PrescriptionDownload ";

const Allcheckup = ({ showHeaderActions = true, showStats = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginUser } = useContext(AuthContext);
  const { userDetails, getUserDetails } = useContext(AuthContext);

  const { doctorDetails, getDoctorDetails } = useContext(AuthContext);

  // State management
  const [checkUps, setCheckUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCheckUp, setSelectedCheckUp] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    startDate: "",
    endDate: "",
    status: "",
    petName: "",
    doctorId: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    pendingPayment: 0,
    totalRevenue: 0,
  });

  // Check if current user is authorized to view all checkups
  const isAuthorized = () => {
    if (!loginUser) return false;
    // Allow admin and doctor roles to view all check-ups
    return loginUser.role === "admin" || loginUser.role === "doctor";
  };

  // Fetch all check-ups

  const fetchCheckUps = async (page = 1, searchFilters = {}) => {
    try {
      setLoading(true);
      setError("");
      const params = {
        page,
        limit: pageSize,
        ...searchFilters,
      };

      console.log("Fetching checkups with params:", params);
      console.log(
        "Login User Details:",
        loginUser?.role === "doctor" ? doctorDetails?._id : userDetails?._id
      );

      let response;

      // Check if the logged-in user is a doctor
      if (loginUser?.role === "doctor" && doctorDetails?._id) {
        // Use doctor-specific API endpoint
        console.log("Fetching checkups for doctor:", doctorDetails._id);
        response = await getCheckUpsByDoctor(doctorDetails._id, params);
      } else {
        // Use general API endpoint for other users
        console.log("Fetching all checkups");
        response = await getAllCheckUp(params);
      }

      console.log("Checkups API response:", response);

      if (response.success) {
        setCheckUps(response.data || []);
        setCurrentPage(response.currentPage || 1);
        setTotalPages(response.totalPages || 1);
        setTotalCount(response.total || 0);
        calculateStats(response.data || []);
      } else {
        setError(response.message || "Failed to fetch check-ups");
      }
    } catch (error) {
      console.error("Error fetching check-ups:", error);
      setError("Failed to fetch check-ups. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const calculateStats = (checkUpData) => {
    const stats = {
      total: checkUpData.length,
      completed: checkUpData.filter((c) => c.status === "completed").length,
      inProgress: checkUpData.filter((c) => c.status === "in-progress").length,
      pendingPayment: checkUpData.filter((c) => c.status === "pending-payment")
        .length,
      totalRevenue: checkUpData.reduce(
        (sum, c) => sum + (c.billing?.totalAmount || 0),
        0
      ),
    };
    setStats(stats);
  };

  // Initial load
  useEffect(() => {
    if (isAuthorized()) {
      fetchCheckUps(1, filters);
    }
  }, [loginUser]);

  // Handle filter changes with debounce
  useEffect(() => {
    if (isAuthorized()) {
      const timeoutId = setTimeout(() => {
        fetchCheckUps(1, filters);
        setCurrentPage(1);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [filters, loginUser]);

  // Apply filters manually
  const applyFilters = () => {
    setCurrentPage(1);
    fetchCheckUps(1, filters);
  };

  // Clear filters
  const clearFilters = () => {
    const emptyFilters = {
      search: "",
      startDate: "",
      endDate: "",
      status: "",
      petName: "",
      doctorId: "",
    };
    setFilters(emptyFilters);
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCheckUps(page, filters);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Format date only
  const formatDateOnly = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get status color and icon
  const getStatusDetails = (status) => {
    switch (status) {
      case "completed":
        return {
          color: "bg-green-100 text-green-800",
          icon: CheckCircle,
          label: "Completed",
        };
      case "in-progress":
        return {
          color: "bg-yellow-100 text-yellow-800",
          icon: Activity,
          label: "In Progress",
        };
      case "pending-payment":
        return {
          color: "bg-red-100 text-red-800",
          icon: AlertCircle,
          label: "Pending Payment",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800",
          icon: FileText,
          label: "Unknown",
        };
    }
  };

  // Handle view details
  const handleViewDetails = (checkUp) => {
    setSelectedCheckUp(checkUp);
    setShowModal(true);
  };

  // Handle edit checkup - navigate to edit form
  const handleEditCheckUp = (checkUp) => {
    console.log("Editing checkup:", checkUp);
    console.log(
      "Appointment ID:",
      checkUp.appointment?._id || checkUp.appointment
    );

    // Get the appointment ID from the checkup
    const appointmentId = checkUp.appointment?._id || checkUp.appointment;

    if (appointmentId) {
      navigate(`/dashboard/check-up/${appointmentId}`);
    } else {
      alert("Unable to edit check-up: Appointment ID not found");
      console.error("Appointment ID not found in checkup:", checkUp);
    }
  };

  const handleDownloadPrescription = (checkUp) => {
    console.log("Downloading prescription for:", checkUp._id);

    // Create prescription download instance
    const prescriptionDownloader = PrescriptionDownload({ checkUp });

    // Trigger the download (opens in new tab with print dialog)
    prescriptionDownloader.downloadPrescription();
  };

  // Optional: Add a "View Prescription" function if you want a preview option
  const handleViewPrescription = (checkUp) => {
    console.log("Viewing prescription for:", checkUp._id);

    // Create prescription download instance
    const prescriptionDownloader = PrescriptionDownload({ checkUp });

    // View the prescription (opens in new tab without auto-print)
    prescriptionDownloader.viewPrescription();
  };

  // Authorization check
  if (!loginUser) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">
            Authentication Required
          </h3>
          <p className="text-gray-500">Please login to view check-ups.</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized()) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
          <p className="text-gray-500">
            You need admin or doctor privileges to view all check-ups.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              All Check-up Records
            </h1>
            <p className="text-gray-600">
              View and manage all check-ups from all doctors
            </p>
          </div>
        </div>
        {showHeaderActions && (
          <div className="flex gap-2">
            <button
              onClick={() => fetchCheckUps(currentPage, filters)}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 flex items-center gap-2 transition-colors"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={() => navigate("/dashboard/all-appointment")}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
            >
              <Plus size={16} />
              New Check-up
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertCircle size={20} className="mr-2 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      {showStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Check-ups
                </p>
                <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.completed}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {stats.inProgress}
                </p>
              </div>
              <Activity className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Pending Payment
                </p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.pendingPayment}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Revenue
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  ₹{stats.totalRevenue.toLocaleString()}
                </p>
              </div>
              <IndianRupee className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by pet name, owner, doctor, or diagnosis..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="min-w-[200px]">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="in-progress">In Progress</option>
              <option value="pending-payment">Pending Payment</option>
            </select>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showFilters
                ? "bg-blue-50 border-blue-300 text-blue-700"
                : "border-gray-300 hover:bg-gray-50"
            }`}
          >
            <Filter size={20} />
            More Filters
          </button>

          {/* Clear Filters */}
          {Object.values(filters).some((value) => value !== "") && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              <X size={20} />
              Clear
            </button>
          )}
        </div>

        {/* Extended Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pet Name
                </label>
                <input
                  type="text"
                  placeholder="Filter by pet name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.petName}
                  onChange={(e) =>
                    setFilters({ ...filters, petName: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Doctor ID
                </label>
                <input
                  type="text"
                  placeholder="Filter by doctor ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.doctorId}
                  onChange={(e) =>
                    setFilters({ ...filters, doctorId: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Check-ups Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pet & Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Doctor
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Diagnosis
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-500">
                        Loading check-ups...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : checkUps.length > 0 ? (
                checkUps.map((checkUp) => {
                  const statusDetails = getStatusDetails(checkUp.status);
                  return (
                    <tr
                      key={checkUp._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-medium text-sm">
                                {checkUp.pet?.name?.charAt(0)?.toUpperCase() ||
                                  "P"}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {checkUp.pet?.name || "Unknown Pet"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {checkUp.pet?.species} • {checkUp.pet?.breed}
                            </div>
                            <div className="text-xs text-gray-400">
                              Owner:{" "}
                              {checkUp.pet?.ownerId?.userDetails?.name ||
                                "Unknown"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Doctor column */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {checkUp.doctor?.name || "Unknown Doctor"}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateOnly(checkUp.createdAt)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(checkUp.createdAt).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs">
                          <p className="truncate">
                            {checkUp.diagnosis?.primary ||
                              checkUp.chiefComplaint ||
                              "Not specified"}
                          </p>
                          {checkUp.chiefComplaint &&
                            checkUp.diagnosis?.primary && (
                              <p className="text-xs text-gray-500 truncate">
                                Complaint: {checkUp.chiefComplaint}
                              </p>
                            )}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDetails.color}`}
                        >
                          <statusDetails.icon size={12} className="mr-1" />
                          {statusDetails.label}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDetails(checkUp)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>

                          <button
                            onClick={() => handleEditCheckUp(checkUp)}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 transition-colors"
                            title="Edit Check-up"
                          >
                            <Edit size={16} />
                          </button>

                          {/* View Prescription Button */}
                          <button
                            onClick={() => handleViewPrescription(checkUp)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 transition-colors"
                            title="View Prescription"
                          >
                            <FileText size={16} />
                          </button>

                          {/* Download/Print Prescription Button */}
                          <button
                            onClick={() => handleDownloadPrescription(checkUp)}
                            className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50 transition-colors"
                            title="Download Prescription"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <FileText size={48} className="text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No check-ups found
                      </h3>
                      <p className="text-gray-500 mb-4">
                        {Object.values(filters).some((value) => value !== "")
                          ? "Try adjusting your filters or search terms."
                          : "No check-ups have been created yet."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-700">
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} to{" "}
            {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
            results
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
              Previous
            </button>

            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (page > totalPages) return null;

                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
                      page === currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showModal && selectedCheckUp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Check-up Details
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Pet Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Pet Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium">
                        {selectedCheckUp.pet?.name || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Species</p>
                      <p className="font-medium">
                        {selectedCheckUp.pet?.species || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Breed</p>
                      <p className="font-medium">
                        {selectedCheckUp.pet?.breed || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Age</p>
                      <p className="font-medium">
                        {selectedCheckUp.pet?.age || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Owner Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Owner Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium">
                        {selectedCheckUp.pet?.ownerId?.userDetails?.name ||
                          "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium">
                        {selectedCheckUp.pet?.ownerId?.userDetails?.phone ||
                          "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Doctor Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Doctor Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium">
                        {selectedCheckUp.doctor?.name || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Specialization</p>
                      <p className="font-medium">
                        {selectedCheckUp.doctor?.specialization || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Check-up Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Check-up Details
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Chief Complaint</p>
                      <p className="font-medium">
                        {selectedCheckUp.chiefComplaint || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Primary Diagnosis</p>
                      <p className="font-medium">
                        {selectedCheckUp.diagnosis?.primary || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Visit Number</p>
                      <p className="font-medium">
                        #{selectedCheckUp.visitNumber || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getStatusDetails(selectedCheckUp.status).color
                        }`}
                      >
                        {getStatusDetails(selectedCheckUp.status).label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Billing Information */}
                {selectedCheckUp.billing && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Billing Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Total Amount</p>
                        <p className="font-medium">
                          ₹
                          {selectedCheckUp.billing.totalAmount?.toLocaleString() ||
                            "0"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Payment Method</p>
                        <p className="font-medium capitalize">
                          {selectedCheckUp.billing.paymentMethod || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Allcheckup;
